import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { formatDate } from "../utils/format";
import { RefreshCcw, Activity, AlertCircle, CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";

const KIND_LABEL = {
  google_ads: "Google Ads",
  google_sheets: "Google Sheets",
  alert_engine: "Alert Engine",
};
const KIND_COLOR = {
  google_ads: "hsl(220 90% 50%)",
  google_sheets: "hsl(150 70% 35%)",
  alert_engine: "hsl(38 92% 50%)",
};

export default function SyncLogs() {
  const [logs, setLogs] = useState([]);
  const [kind, setKind] = useState("");
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (kind) params.kind = kind;
      const { data } = await api.get("/sync-logs", { params });
      setLogs(data || []);
    } catch (e) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, limit]);

  const counts = logs.reduce(
    (acc, l) => {
      acc.total += 1;
      acc[l.ok ? "ok" : "fail"] += 1;
      acc.kinds[l.kind] = (acc.kinds[l.kind] || 0) + 1;
      return acc;
    },
    { total: 0, ok: 0, fail: 0, kinds: {} }
  );

  return (
    <Layout
      title="Sync Logs"
      subtitle="Unified Google Ads + Google Sheets + Alert engine activity feed"
      actions={
        <button
          onClick={load}
          data-testid="sync-logs-refresh"
          className="h-9 px-3 flex items-center gap-1.5 text-xs font-medium border border-border bg-card hover:bg-secondary rounded-sm"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      }
    >
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          data-testid="sync-logs-kind-filter"
          className="h-9 px-3 border border-border bg-card rounded-sm text-sm"
        >
          <option value="">All sources</option>
          <option value="google_ads">Google Ads</option>
          <option value="google_sheets">Google Sheets</option>
          <option value="alert_engine">Alert Engine</option>
        </select>
        <select
          value={limit}
          onChange={(e) => setLimit(parseInt(e.target.value, 10))}
          className="h-9 px-3 border border-border bg-card rounded-sm text-sm"
        >
          <option value={50}>Last 50</option>
          <option value={100}>Last 100</option>
          <option value={250}>Last 250</option>
          <option value={500}>Last 500</option>
        </select>

        <div className="flex-1" />
        <SummaryPill label="Total" value={counts.total} />
        <SummaryPill label="OK" value={counts.ok} tone="success" />
        <SummaryPill label="Failed" value={counts.fail} tone={counts.fail > 0 ? "critical" : "muted"} />
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="sync-logs-table">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th style={{ width: 38 }}></th>
              <th style={{ width: 130 }}>Source</th>
              <th style={{ width: 70 }}>Status</th>
              <th>Message</th>
              <th style={{ width: 200 }}>Counters</th>
              <th style={{ width: 160 }}>When</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l, i) => {
              const key = `${l.kind}-${l.at}-${i}`;
              const isOpen = !!expanded[key];
              const p = l.payload || {};
              const counters = formatCounters(l.kind, p);
              return (
                <React.Fragment key={key}>
                  <tr
                    className="cursor-pointer hover:bg-secondary/30"
                    onClick={() => setExpanded({ ...expanded, [key]: !isOpen })}
                    data-testid={`sync-log-row-${i}`}
                  >
                    <td>{isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}</td>
                    <td>
                      <span
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm"
                        style={{ color: KIND_COLOR[l.kind], backgroundColor: `${KIND_COLOR[l.kind]}15` }}
                      >
                        {KIND_LABEL[l.kind] || l.kind}
                      </span>
                    </td>
                    <td>
                      {l.ok ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--success))]">
                          <CheckCircle2 className="w-3.5 h-3.5" /> OK
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--critical))]">
                          <AlertCircle className="w-3.5 h-3.5" /> Failed
                        </span>
                      )}
                    </td>
                    <td className="text-sm">{l.message}</td>
                    <td className="font-mono text-[11px] text-muted-foreground">{counters}</td>
                    <td className="text-xs text-muted-foreground">{formatDate(l.at)}</td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-secondary/20">
                      <td></td>
                      <td colSpan={5} className="py-2">
                        <pre className="text-[11px] font-mono whitespace-pre-wrap break-all overflow-x-auto p-3 bg-card border border-border rounded-sm max-h-64">
                          {JSON.stringify(p, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {logs.length === 0 && (
              <tr>
                <td colSpan={6} className="p-12 text-center text-sm text-muted-foreground">
                  <Activity className="w-6 h-6 mx-auto mb-2 opacity-40" />
                  {loading ? "Loading sync activity…" : "No sync activity recorded yet. Run a manual sync to populate this feed."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}

function formatCounters(kind, p) {
  if (kind === "google_ads") {
    const i = p.campaigns_inserted ?? 0;
    const u = p.campaigns_updated ?? 0;
    const m = (p.metrics_inserted ?? 0) + (p.metrics_updated ?? 0);
    const c = p.call_leads_inserted ?? 0;
    const parts = [];
    if (i || u) parts.push(`camp +${i}~${u}`);
    if (m) parts.push(`metrics ${m}`);
    if (c) parts.push(`calls +${c}`);
    return parts.join(" · ") || "—";
  }
  if (kind === "google_sheets") {
    const r = p.total_rows ?? 0;
    const i = p.inserted ?? 0;
    const s = p.skipped ?? 0;
    return r ? `rows ${r} · +${i} · ~${s}` : "—";
  }
  if (kind === "alert_engine") {
    return `${p.new_alerts ?? 0} new`;
  }
  return "";
}

function SummaryPill({ label, value, tone = "primary" }) {
  const map = {
    primary: "border-border text-foreground",
    success: "border-[hsl(var(--success))]/40 text-[hsl(var(--success))]",
    critical: "border-[hsl(var(--critical))]/40 text-[hsl(var(--critical))]",
    muted: "border-border text-muted-foreground",
  };
  return (
    <div className={`inline-flex items-center gap-2 px-3 h-9 border rounded-sm bg-card ${map[tone]}`}>
      <span className="label-mono">{label}</span>
      <span className="text-sm font-semibold tabular">{value}</span>
    </div>
  );
}
